import { useNavigate } from 'react-router';
import type { CardMenuItem } from '@tempmee/core';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Column,
  Tab,
  TabPanel,
  Tabs,
  Typography,
} from '@tempmee/react';
import { AdditionalDetails } from '@tempmee/react-blocks';
import { RichTextViewer } from '~/commonComponents';
import { DetailsPageShell } from '~/components/shared/DetailsPage';
import { IJobPostDetailEntity, JobPostingStatus } from '~/domain/PermanentJobs';
import { firePermJobOverviewAddInterviewsClicked } from '~/posthogs/events';
import { CreateOnsiteInterviewSteps } from '../CreateOnsiteInterview';
import { ClosedBanner } from '../JobOverview/components/JobOverviewHeader/ClosedBanner';
import { SuspendedBanner } from '../JobOverview/components/JobOverviewHeader/SuspendedBanner';
import { TAB } from '../JobOverview/useJobOverview';
import { CandidatesTab, InterviewsTab, JobOverviewCard } from './components';
import { toPostingAdditionalDetailsProps } from './postingAdditionalDetails';
import { POSTING_DETAILS_COPY } from './postingDetailsRules';

interface IPostingDetailsProps {
  postingId: string;
  jobPosting?: IJobPostDetailEntity;
  /** Display name of the posting's profession, e.g. "Dental Hygienist". */
  professionDisplay: string;
  activeTab: TAB;
  onChangeTab: (tab: string) => void;
  /** Starts the shared Close Job flow — its popups stay with the Job Overview route. */
  onClosePosting: () => void;
  /** The header's back arrow. The route picks the destination, as the legacy page's did. */
  onBack: () => void;
  hasScheduledOnsiteInterviews: boolean;
}

/**
 * Posting Details — the redesigned Job Overview page (GOTU-8150), behind
 * `GOTU-8153-deb-office-updates`. The ⋯ menu replaces the job status select and the
 * Job Details tab; the posting's summary, Additional Information and Job Description
 * move to the sidebar. Deb's Progress is not drawn: no office-facing source for it exists.
 */
export const PostingDetails = ({
  postingId,
  jobPosting,
  professionDisplay,
  activeTab,
  onChangeTab,
  onClosePosting,
  onBack,
  hasScheduledOnsiteInterviews,
}: IPostingDetailsProps) => {
  const navigate = useNavigate();
  const status = jobPosting?.status as JobPostingStatus;

  // Only a live posting can be edited, get more interviews or be closed — the same
  // rule that showed the job status select only while Active.
  const actions: CardMenuItem[] =
    status === JobPostingStatus.Active
      ? [
          {
            label: POSTING_DETAILS_COPY.editPosting,
            testID: 'posting-menu-edit',
            onSelect: () =>
              navigate(`/permanent-jobs/job-overview/${postingId}/${TAB.JOB_DETAILS}`, {
                state: { fromPostingDetails: true },
              }),
          },
          {
            label: POSTING_DETAILS_COPY.addInterview,
            testID: 'posting-menu-add-interview',
            onSelect: () => {
              firePermJobOverviewAddInterviewsClicked({ postingId });
              const interviewsPath = `/permanent-jobs/postings/${postingId}/onsite-interviews`;
              navigate(`${interviewsPath}/create/${CreateOnsiteInterviewSteps.WorkDetails}`);
            },
          },
          {
            label: POSTING_DETAILS_COPY.closePosting,
            testID: 'posting-menu-close',
            onSelect: onClosePosting,
          },
        ]
      : [];

  return (
    <DetailsPageShell
      title={POSTING_DETAILS_COPY.title}
      actions={actions}
      actionsLabel={POSTING_DETAILS_COPY.actionsLabel}
      onBack={onBack}
      sidebarGap={4}
      testIDPrefix="posting-details"
      summary={
        <JobOverviewCard
          title={jobPosting?.title || ''}
          profession={professionDisplay}
          publishedAt={jobPosting?.publishedAt}
          officeName={jobPosting?.officeName || ''}
          status={status}
        />
      }
      primary={
        <Column gap={4} className="w-full" data-cy="posting-details">
          <ClosedBanner
            hasScheduledOnsiteInterviews={hasScheduledOnsiteInterviews}
            jobStatus={status}
          />
          <SuspendedBanner
            jobStatus={status}
            officeId={jobPosting?.officeId || undefined}
            officePlanId={jobPosting?.officePlanId || undefined}
          />
          <Tabs value={activeTab} onChange={onChangeTab} className="w-full gap-2">
            <Tab value={TAB.CANDIDATES} data-cy="candidates-tab">
              {POSTING_DETAILS_COPY.candidatesTab}
            </Tab>
            <Tab value={TAB.WORKING_INTERVIEWS} data-cy="onsite-interviews-tab">
              {POSTING_DETAILS_COPY.interviewsTab}
            </Tab>
            <TabPanel value={TAB.CANDIDATES} className="w-full">
              <CandidatesTab jobPostingId={postingId} />
            </TabPanel>
            <TabPanel value={TAB.WORKING_INTERVIEWS} className="w-full pt-2">
              <InterviewsTab
                jobPostingId={postingId}
                officeId={jobPosting?.officeId || undefined}
                officeName={jobPosting?.officeName || ''}
                profession={jobPosting?.subProfession || jobPosting?.professionType || undefined}
              />
            </TabPanel>
          </Tabs>
        </Column>
      }
      sidebar={
        jobPosting ? (
          <Column gap={4}>
            <AdditionalDetails
              variant="posting"
              defaultOpen={false}
              data-cy="posting-additional-details"
              {...toPostingAdditionalDetailsProps(jobPosting)}
            />
            <Accordion
              className="w-full rounded-lg bg-background"
              data-cy="posting-job-description"
            >
              <AccordionSummary className="px-3">
                <Typography variant="bodyLarge" as="span" className="text-text-secondary">
                  {POSTING_DETAILS_COPY.jobDescription}
                </Typography>
              </AccordionSummary>
              <AccordionDetails className="px-3 pb-3">
                <RichTextViewer
                  content={jobPosting.description}
                  className="rich-text-viewer--body1"
                />
              </AccordionDetails>
            </Accordion>
          </Column>
        ) : null
      }
    />
  );
};
